// Panel metadata — separated from lazy imports to avoid circular dependencies with ToolHub.
// ToolHub imports from here; panelRegistry re-exports from here.

export interface PanelMenuOption {
  value: string;
  label: string;
  description?: string;
  /**
   * Role-based menu visibility (orthogonal to the per-user allowedPanels ACL):
   * - 'admin'   → only shown to role 'admin' (and when auth is disabled)
   * - 'partner' → hidden for role 'admin' (the admin has the same content
   *               elsewhere, e.g. Feedback lives as tab inside the Partner-Inbox)
   * - undefined → shown to everyone the ACL allows
   */
  visibility?: 'admin' | 'partner';
}
export interface PanelMenuGroup { category: string; color: string; items: PanelMenuOption[] }

/**
 * PANEL_MENU_OPTIONS filtered by the role-based visibility flag.
 * `role` null/undefined (auth disabled / dev) is treated as admin.
 * The per-user ACL (canAccessPanel) is applied separately by the callers.
 */
export function menuGroupsForRole(role: string | null | undefined): PanelMenuGroup[] {
  const isAdminView = !role || role === 'admin';
  return PANEL_MENU_OPTIONS
    .map(g => ({
      ...g,
      items: g.items.filter(i =>
        i.visibility === 'admin' ? isAdminView :
        i.visibility === 'partner' ? !isAdminView :
        true,
      ),
    }))
    .filter(g => g.items.length > 0);
}

export const PANEL_NAMES: Record<string, string> = {
  cui: 'Chat',
  'prod-chat': 'Prod Chat',
  'cui-lite': 'Chat',
  'coach-chat': 'Coach-Chat',
  browser: 'Browser',
  preview: 'File Preview',
  notes: 'Notes',
  images: 'Images',
  mission: 'Mission Control',
  'mission-chat': 'Mission Chat',
  office: 'Virtual Office',
  'virtual-office': 'Virtual Office',
  gmail: 'Virtual Office',
  knowledge: 'Knowledge',
  'knowledge-fullscreen': 'Knowledge',
  'platform-admin': 'Platform Admin',
  linkedin: 'Marketing',
  'qa-dashboard': 'QA Dashboard',
  'bridge-monitor': 'Bridge Monitor',
  'infisical-monitor': 'Infisical Monitor',
  'repo-dashboard': 'Git & Pipeline Monitor',
  watchdog: 'Dev Server Watchdog',
  infrastructure: 'Dev Server Watchdog',
  'background-ops': 'Background Ops',
  'conversation-queue': 'Conversation Queue',
  maintenance: 'Maintenance',
  'input-audit': 'Input Audit',
  architecture: 'Architecture Explorer',
  'report-builder': 'Report Builder',
  'prompt-explorer': 'Workflow Explorer',
  'business-angel': 'Business Angel',
  'privat-angel': 'Privat Angel',
  'business-docs': 'Business Docs',
  'business-navigator': 'Business-Ordner',
  'sales': 'Sales',
  'business-overview': 'Business-Übersicht',
  'my-tasks': 'My Tasks',
  'focus': 'Fokus',
  'energy-cases': 'Energy Cases',
  'report-cases': 'Report Cases',
  'activity-feed': 'Activity Feed',
  'partner-inbox': 'Partner Inbox',
  'group-chat': 'Gruppenraum',
  'deck-annotate': 'Businessplan-Deck',
  'report-annotate': 'Bericht-Anmerkungen',
  'customer-comms': 'Kundenkommunikation',
  'netzwerk': 'Netzwerk',
  feedback: 'Feedback',
  'team-status': 'Team Status',
  'uploads': 'Uploads',
  'calendar': 'Kalender',
  'scheduling': 'Terminfindung',
  'mail': 'Mail',
  'postfaecher': 'Postfächer',
  'tool-hub': 'Tool Hub',
  'error-monitor': 'Error Monitor',
  'deploy': 'Deploy',
  'partner-browsers': 'Partner-Browser',
  'partner-server': 'Partner Server Health',
  'sandbox-privat-angel': 'Privat-Assistent (Sandbox)',
  'sandbox-business-angel': 'Business-Assistent (Sandbox)',
  'sandbox-rafael-angel': 'Rafael-Assistent',
};

export const PANEL_MENU_OPTIONS: PanelMenuGroup[] = [
  {
    category: 'Workspace',
    color: 'var(--tnf-7aa2f7)',
    items: [
      { value: 'cui', label: 'Chat', description: 'Konversation mit der KI — Aufträge stellen, Sub-Sessions starten, Antworten lesen.' },
      { value: 'browser', label: 'Browser', description: 'Eingebetteter Browser für Apps und Tools — direkt im Layout, ohne Tab-Wechsel.' },
      { value: 'tool-hub', label: 'Tool Hub', description: 'Übersicht aller verfügbaren Panels — schneller Einstieg per Klick.' },
      { value: 'preview', label: 'File Preview', description: 'Markdown- und Dokument-Vorschau — Strategien, Specs, Notizen rendern.' },
      { value: 'notes', label: 'Notes', description: 'Persönliche und geteilte Notizen — pro Workspace abgelegt.' },
      { value: 'images', label: 'Images', description: 'Bild-Galerie für Screenshots und Uploads des aktuellen Workspaces.' },
      { value: 'uploads', label: 'Dateien hochladen', description: 'Dokumente, Bilder, PDFs für deinen Chat hochladen.' },
    ],
  },
  {
    category: 'Partner',
    color: 'var(--tnf-e0af68)',
    items: [
      { value: 'business-overview', label: 'Business-Übersicht', description: 'Wo stehen wir gerade, was sind die Ziele — auf einen Blick, für alle.' },
      { value: 'sales', label: 'Sales', description: 'Öffentlicher Kontakt-Log: wer wurde von wem wann kontaktiert — Dubletten vermeiden.' },
      { value: 'partner-inbox', label: 'Nachrichten', description: 'Posteingang — Antworten von Rafael auf deine Fragen, Updates vom Team.' },
      { value: 'group-chat', label: 'Gruppenraum', description: 'Gemeinsamer Chat für alle Partner — für alles, was nicht 1:1 mit Rafael, sondern mit allen besprochen werden soll.' },
      { value: 'deck-annotate', label: 'Businessplan-Deck', description: 'Das Businessplan-Deck ansehen und Folie für Folie kommentieren — Diskussion sichtbar für alle Partner.' },
      // customer-comms is backend-gated adminOrInternal — partners only ever saw an
      // empty panel here, so the menu entry is admin-only too.
      { value: 'customer-comms', label: 'Kundenkommunikation', description: 'Alle Meldungen & E-Mail-Antworten pro Kunde — deine persönliche 1:1-Schnittstelle zum Kunden.', visibility: 'admin' },
      // Personenbezogene Daten Dritter — Backend ist adminOrInternal, der
      // Menueeintrag muss dazu passen.
      { value: 'netzwerk', label: 'Netzwerk', description: 'Wer ist wer im WerkING-Umfeld: Personen, Firmen, Beziehungen — als Graph, Liste und Cluster. Nur lesen, Quelle sind die Netzwerk-Dossiers.' },
      // Die Fernbrowser halten Rafaels claude.ai-Anmeldungen — deshalb admin-only,
      // hier waere jeder Partner-Zugriff ein Konto-Zugriff.
      { value: 'partner-browsers', label: 'Partner-Browser', description: 'Die vier dauerhaft angemeldeten Browser auf dem Partner-Server — je einer pro Claude-Konto. Ein Klick öffnet den Fernbrowser hier im Panel; die Kachel zeigt vorher, ob er überhaupt erreichbar ist.', visibility: 'admin' },
      // Partners submit feedback here; the admin triages it via the Feedback-Tab
      // inside the Partner-Inbox (one inbox, one badge) — panel hidden for admin.
      { value: 'feedback', label: 'Feedback an Rafael', description: 'Verbesserungsvorschläge, Bugs oder Fragen einreichen — landet in Rafaels Inbox.', visibility: 'partner' },
      { value: 'focus', label: 'Fokus', description: 'Tagesfokus + Themen mit Fortschrittsbalken. KI-Sessions haken erledigte Schritte selbst ab — du schaust dem Progress zu.' },
      // Ohne visibility-Flag = fuer Rafael UND die Sales-Partner sichtbar
      // (Rafael 03.09.2026: Plasser bekommt die volle Sicht). Backend ist
      // passend dazu authOrInternal.
      { value: 'linkedin', label: 'Marketing', description: 'Die Kampagnen-Kette von der Reichweite bis zum Check: jede Stufe gegen ihr Ziel, dazu Netzwerk, die nächsten Kandidaten und Entwürfe. Nur lesend — es wird nichts versendet.' },
      { value: 'energy-cases', label: 'Energy Cases', description: 'Welche Energy-Datensätze gibt es, woher stammen sie — und welche Berichte sind daraus entstanden. Links Datensatz mit Vorschau, rechts die Berichte zum Öffnen und Herunterladen.' },
      { value: 'report-cases', label: 'Report Cases', description: 'Testfälle je Report-Dokumenttyp: welche Eingaben liegen vor, was ist die erwartete Wahrheit, welcher Bericht ist daraus entstanden. Ein Typ ohne bewerteten Bericht gilt als ungeprüft.' },
      { value: 'business-docs', label: 'Produkt-Doku', description: 'Vision, Roadmap, Feature-Specs — was wird gerade gebaut und warum.' },
      // Der ganze Business-Ordner samt Finanzunterlagen — Backend ist nur Admin/intern.
      { value: 'business-navigator', label: 'Business-Ordner', description: 'Was liegt im Business-Ordner: Baum links, Lesefläche rechts, Prüfstand jeder Datei farbig. Nur lesen.', visibility: 'admin' },
      { value: 'activity-feed', label: 'Aktivität', description: 'Was hat das System zuletzt gemacht — Sessions, Commits, Deploys.' },
      // my-tasks und team-status wurden gerendert (ToolHub) und in users.json
      // vergeben, hatten hier aber keinen Eintrag — dadurch waren sie fuer
      // NIEMANDEN im Tool Hub erreichbar, auch nicht fuer Partner, denen sie
      // laengst zugewiesen sind. Nachgetragen 14.08.2026.
      { value: 'my-tasks', label: 'Aufgaben', description: 'Offene Punkte festhalten und abhaken — was noch zu tun ist, mit Zuständigkeit und Fälligkeit.' },
      { value: 'team-status', label: 'Team-Status', description: 'Wer arbeitet gerade woran — Überblick über die laufende Arbeit im Team.' },
      { value: 'calendar', label: 'Kalender', description: 'Termine und Deadlines — synchronisiert mit dem Konto-Kalender.' },
      { value: 'scheduling', label: 'Terminfindung', description: 'Doodle-artige Terminumfrage: Zeit-Optionen vorschlagen, Teilnehmer stimmen per Link ab (ohne Login), Gewinner festlegen → Kalendereinladung (.ics) an alle.' },
      // Rafael's real personal mailbox (IONOS office@werking.tools) — backend
      // now enforces requireRole('admin') too (mail.ts), but the menu flag
      // must match so partners never even see the tab.
      { value: 'mail', label: 'Mail', description: 'E-Mail-Postfach — lesen, beantworten, Drafts vorbereiten.', visibility: 'admin' },
      // Alle vier Postfächer + Kalender über Nylas, nur lesend. Backend: nur Haus Dev, admin.
      { value: 'postfaecher', label: 'Postfächer', description: 'Alle vier Postfächer und Kalender (engelmann.at, Gmail, data-energyneering, werking.tools) — nur lesen.', visibility: 'admin' },
    ],
  },
  {
    category: 'Übersicht',
    color: 'var(--tnf-bb9af7)',
    items: [
      { value: 'mission', label: 'Mission Control', description: 'Steuerzentrale für alle Sessions — starten, beenden, Layouts setzen.' },
      { value: 'mission-chat', label: 'Mission Chat', description: 'Quer-Chat über alle Sessions hinweg — analysiert, synchronisiert, fragt nach.' },
      { value: 'conversation-queue', label: 'Conversation Queue', description: 'Warteschlange laufender und geplanter KI-Konversationen.' },
      { value: 'office', label: 'Virtual Office', description: 'Visualisierung wer woran arbeitet — Sessions, Tasks, Status auf einen Blick.' },
    ],
  },
  {
    category: 'Analyse',
    color: 'var(--tnf-7dcfff)',
    items: [
      { value: 'qa-dashboard', label: 'QA Dashboard', description: 'Test-System: Szenarien, Reports, Pyramide, Coverage. Beschreibungen + Wissensbasis inklusive.' },
      { value: 'architecture', label: 'Architecture Explorer', description: 'Repo- und Modul-Architektur visualisieren — Abhängigkeiten und Schichten.' },
      { value: 'knowledge', label: 'Knowledge', description: 'Wissensbasis: gesammelte Erkenntnisse aus Sessions, Memory und Docs.' },
      { value: 'report-builder', label: 'Report Builder', description: 'Berichte zusammenstellen aus Daten und Texten — fürs Management.' },
      { value: 'prompt-explorer', label: 'Workflow Explorer', description: 'Workflow-Manifeste durchsuchen und prüfen — Energy, Safety, Report.' },
    ],
  },
  {
    category: 'Monitoring',
    color: 'var(--tnf-ff9e64)',
    items: [
      { value: 'error-monitor', label: 'Error Monitor', description: 'Aktuelle Fehler aus allen Apps — Stack-Traces, Häufungen, Triage.' },
      { value: 'bridge-monitor', label: 'Bridge Monitor', description: 'AI-Bridge-Status: Modelle, Quoten, laufende Anfragen, Fehlerquote.' },
      { value: 'repo-dashboard', label: 'Git & Pipeline Monitor', description: 'Status aller Repos: Branches, offene PRs, Build-Status, Deploy-Stand.' },
      { value: 'infisical-monitor', label: 'Infisical Monitor', description: 'Secret-Stand pro Projekt — Drift zwischen dev/prod erkennen.' },
      { value: 'watchdog', label: 'Dev Server Watchdog', description: 'Laufende Prozesse, Ports, Speicher und CPU des Dev-Servers.' },
    ],
  },
  {
    category: 'Admin',
    color: 'var(--tnf-f7768e)',
    items: [
      { value: 'platform-admin', label: 'Platform Admin', description: 'Zentrale Verwaltung aller Apps über die Bridge: Usage, Users, Tenants, Billing, Activity, Feedback.' },
      { value: 'deploy', label: 'Deploy', description: 'Production-Deploy per Klick: App wählen, Passwort + Bestätigungen im Terminal tippen, Live-Output zusehen. Nur Deploy-Account.' },
      { value: 'prod-chat', label: 'Prod Chat', description: 'Chat mit Produktions-Rechten — erst nach Passwort-Freigabe, dann für die gewählte Dauer (30 Min. bis 2 Std.) offen.', visibility: 'admin' },
      { value: 'business-angel', label: 'Business Angel', description: 'Geschäftliche Anfragen und To-Dos automatisch beantworten lassen.' },
      { value: 'privat-angel', label: 'Privat Angel', description: 'Privater Assistent für persönliche Aufgaben und Korrespondenz.' },
      { value: 'sandbox-rafael-angel', label: 'Rafael-Assistent', description: 'Vereinter Assistent für Rafael: Privat + Business in einer Session, cross-domain Konversationen möglich.' },
      { value: 'sandbox-business-angel', label: 'Business-Assistent (Sandbox)', description: 'Nur Business-Wissensbase. Empfehlung: stattdessen Rafael-Assistent nutzen.' },
      { value: 'sandbox-privat-angel', label: 'Privat-Assistent (Sandbox)', description: 'Nur Privat-Wissensbase. Empfehlung: stattdessen Rafael-Assistent nutzen.' },
      { value: 'background-ops', label: 'Background Ops', description: 'Hintergrund-Jobs anschauen: Cron, Sync-Bots, periodische Tasks.' },
      { value: 'maintenance', label: 'Maintenance', description: 'Wartungs-Routinen ausführen: Dokumente, Caches, Aufräum-Skripte.' },
      { value: 'input-audit', label: 'Input Audit', description: 'Alle eigenen Eingaben über alle Sessions — Volltext-Suche, Zeitfilter.' },
      { value: 'partner-server', label: 'Partner Server Health', description: 'Status des Partner-Servers: Services, Sync-Stand, Logs.' },
    ],
  },
];
